import * as React from 'react';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import { FormField } from 'components/form';

import ChannelSwitch from 'modules/influencers/components/channels/channel-switch/ChannelSwitch';

import { __t, applyTranslations } from 'utils/language';
import { isArrayRequired } from 'utils/validators';
import { INFLUENCER_CHANNELS_ORDER_NEW } from 'utils/influencers';

import './profile-channels.scss';

applyTranslations(require('./profile-channels.i18n.json'));

import type { ChannelForm, InfluencerProfileForm } from 'types';
import type {
  ChannelSwitchExpiredAnalytics
} from 'modules/influencers/components/channels/channel-switch/types';

interface Props {
  profile: InfluencerProfileForm,
  hasError: boolean,
  expiredAnalyticsDataForInfluencer: ChannelSwitchExpiredAnalytics,
  isCreationStep?: boolean,
  isAdmin?: boolean,
  onChange: (profile: Partial<InfluencerProfileForm>) => void
}

const MSG_KEY = 'profile_channels';

const ProfileChannels: React.FC<Props> = ({
  profile,
  isAdmin,
  isCreationStep,
  hasError,
  expiredAnalyticsDataForInfluencer,
  onChange
}) => {
  const channels = profile.channels;

  if (isNil(channels)) {
    return null;
  }

  const processedChannels = INFLUENCER_CHANNELS_ORDER_NEW.map(type => (
    channels.find(c => c.type === type) || { type, url: '' }
  ));

  const columnOne = processedChannels.slice(0, 4);
  const columnTwo = processedChannels.slice(4);

  const validatableChannels = processedChannels.filter(c =>
    (c.type === 'instagram' && !isEmpty(c.url))
    || (c.type === 'youtube' && !isEmpty(c.url))
    || (c.type === 'tiktok' && !isEmpty(c.url))
  );

  const handleChange = (
    channel: ChannelForm,
    isToggled?: boolean
  ): void => {
    const foundChannel = channels.find(c => c.type === channel.type);
    let updatedChannels: Array<ChannelForm> = [];

    if (!foundChannel) {
      updatedChannels = [...channels, channel];
    } else if (!isToggled) {
      updatedChannels = channels
        .map(c => (c.type === channel.type ? channel : c));
    } else {
      updatedChannels = channels
        .filter(c => (c.type !== channel.type));
    }

    onChange({
      channels: updatedChannels
    });
  };

  const renderChannel = (
    channel: ChannelForm
  ): React.ReactNode => (
    <div
      key={`profile-channels__channel--${channel.type}`}
      className="profile-channels__channel"
    >
      <ChannelSwitch
        channel={channel}
        isAdmin={isAdmin}
        hasError={hasError}
        errorMessage={channel.isInvalid ? __t(`${MSG_KEY}.errors.non_existent_url`) : null}
        expiredAnalyticsDataForInfluencer={expiredAnalyticsDataForInfluencer}
        onChange={handleChange}
        isCreationStep={isCreationStep}
      />
    </div>
  );

  return (
    <div className="profile-channels">
      <FormField
        direction="column"
        showValidationIcon={false}
        className="profile-channels__column-one"
        validators={[
          isArrayRequired(validatableChannels, __t(`${MSG_KEY}.errors.validation`))
        ]}
      >
        {columnOne.map(renderChannel)}
      </FormField>
      <FormField
        direction="column"
        showValidationIcon={false}
        className="profile-channels__column-two"
      >
        {columnTwo.map(renderChannel)}
      </FormField>
    </div>
  );
};

export default ProfileChannels;
